import { FC } from 'react';
import useQueryNoCache from 'utils/hooks/useQueryNoCache';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import {
  constructFormState,
  PostComposerFormState,
} from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import PageApi from 'api/page';
import { PostModalQueryParams } from 'app/modules/posts/modules/post/hooks/usePostModal';
import { usePostsContainer } from 'components/posts/context';

type Props = {
  queryParams: PostModalQueryParams;
  postIdToEdit?: number;
  children(params: {
    isLoading: boolean;
    isError: boolean;
    isEditing: boolean;
    initialFormState: PostComposerFormState | undefined;
  }): JSX.Element;
};

const PostComposerPreloader: FC<Props> = ({ queryParams, children }) => {
  const isEditing = !!queryParams.id;
  const { pages, isPagesLoading } = usePostsContainer();

  const postToEdit = useQueryNoCache(
    [queryKey.postComposerDetail, queryParams.id],
    () => PostApi.detail(queryParams.id!),
    { enabled: isEditing },
  );

  const page = useQueryNoCache(
    queryKey.pageDetail(queryParams.profileIds[0]),
    () => PageApi.detail(queryParams.profileIds[0]),
    { enabled: !!queryParams.profileIds[0] },
  );

  function getInitialFormState() {
    if (isEditing && postToEdit.data) {
      return constructFormState({ post: postToEdit.data });
    }

    if (page.data) {
      return constructFormState({ queryParams, page: page.data });
    }
  }

  return children({
    isLoading:
      postToEdit.isLoading ||
      page.isLoading ||
      (!pages.length && isPagesLoading),
    isError:
      postToEdit.isError ||
      page.isError ||
      (!pages.length && !isPagesLoading) ||
      (!page.isLoading && !page.data) ||
      (!postToEdit.isLoading && !postToEdit.data),
    isEditing,
    initialFormState: getInitialFormState(),
  });
};

export default PostComposerPreloader;
